import { useCallback, useRef, useState } from 'react';
import { DependencyGraph, DependencyNode, RegistrationAPI } from './types';
import { ApproachStableId } from '../useApproachStableIds/types';

/**
 * Hook that manages the approach dependency graph.
 * Tracks which approaches depend on PC approaches for cascading updates.
 *
 * @returns RegistrationAPI for managing approach dependencies
 *
 * @example
 * const { register, getDependents } = useApproachDependencyGraph();
 *
 * // Register a backsolve that depends on a PC approach
 * register({
 *   approachId: backsolveStableId,
 *   approachType: 'Backsolve',
 *   panelId: approach.panelId,
 *   sourceOfTruthStableId: approach.panelId,
 *   sourceOfTruthType: 'Public Comps',
 * });
 *
 * // Later, when PC changes in a relevant way, get all dependents
 * const dependents = getDependents(gpcStableId);
 */
const useApproachDependencyGraph = (): RegistrationAPI => {
  // State: Map of source of truth approaches stable IDs to sets of dependent stable IDs
  const [graph, setGraph] = useState<DependencyGraph>(new Map());

  // Ref: Store full node metadata for lookups
  const nodeMetadataRef = useRef<Map<ApproachStableId, DependencyNode>>(new Map());

  // Avoid registering with same source of truth type and id
  // If registering with same type and different id, update graph to keep dependents in sync
  const register = useCallback((node: DependencyNode) => {
    const previousNode = nodeMetadataRef.current.get(node.approachId);
    const previousSourceStableId = previousNode?.sourceOfTruthStableId;
    const previousSourceType = previousNode?.sourceOfTruthType;

    // Store node metadata
    nodeMetadataRef.current.set(node.approachId, node);
    const { sourceOfTruthStableId: sourceStableId, sourceOfTruthType } = node;

    const hasSameSource =
      previousSourceStableId === sourceStableId &&
      previousSourceType === sourceOfTruthType &&
      Boolean(sourceStableId) &&
      Boolean(sourceOfTruthType);

    if (hasSameSource) {
      return;
    }

    setGraph(prevGraph => {
      const newGraph = new Map(prevGraph);

      // different source of truth, otherwise it would have returned already
      if (previousSourceType && previousSourceType === sourceOfTruthType && previousSourceStableId) {
        const previousDependents = newGraph.get(previousSourceStableId);
        if (previousDependents) {
          previousDependents.delete(node.approachId);
          if (previousDependents.size === 0) {
            newGraph.delete(previousSourceStableId);
          }
        }
      }

      if (sourceStableId && sourceOfTruthType) {
        const dependents = newGraph.get(sourceStableId) || new Set<ApproachStableId>();
        dependents.add(node.approachId);
        newGraph.set(sourceStableId, dependents);
      }

      return newGraph;
    });
  }, []);

  const unregister = useCallback((approachId: ApproachStableId) => {
    // Remove from node metadata
    nodeMetadataRef.current.delete(approachId);

    // Remove from all dependency sets in the graph
    setGraph(prevGraph => {
      const newGraph = new Map(prevGraph);

      // Remove as a dependent from all GPC approaches
      newGraph.forEach((dependents, gpcId) => {
        dependents.delete(approachId);
        if (dependents.size === 0) {
          newGraph.delete(gpcId);
        }
      });

      // If this was a GPC approach, remove its entire entry
      newGraph.delete(approachId);

      return newGraph;
    });
  }, []);

  const getDependents = useCallback(
    (gpcApproachId: ApproachStableId): DependencyNode[] => {
      const dependentIds = graph.get(gpcApproachId);

      if (!dependentIds || dependentIds.size === 0) {
        return [];
      }

      // Map stable IDs to full node metadata
      const dependents: DependencyNode[] = [];
      dependentIds.forEach(dependentId => {
        const metadata = nodeMetadataRef.current.get(dependentId);
        if (metadata) {
          dependents.push(metadata);
        }
      });

      return dependents;
    },
    [graph]
  );

  const getGraph = useCallback((): DependencyGraph => {
    // Return a deep copy to prevent external mutations
    const graphCopy = new Map<ApproachStableId, Set<ApproachStableId>>();
    graph.forEach((dependents, gpcId) => {
      graphCopy.set(gpcId, new Set(dependents));
    });
    return graphCopy;
  }, [graph]);

  const clear = useCallback(() => {
    setGraph(new Map());
    nodeMetadataRef.current.clear();
  }, []);

  const readNode = useCallback(
    (approachId: ApproachStableId): DependencyNode | null => nodeMetadataRef.current.get(approachId) || null,
    []
  );

  return {
    register,
    unregister,
    readNode,
    getDependents,
    getGraph,
    clear,
  };
};

export default useApproachDependencyGraph;
